import React, { useState } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWhitelabel } from '../context/WhitelabelContext';
import api, { handleCatch } from '../api';

const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobile, userName } = location.state || {};
  const { config: whitelabelConfig } = useWhitelabel();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const primaryColor = whitelabelConfig?.primaryColor || '#0066CC';
  const secondaryColor = whitelabelConfig?.secondaryColor || '#004C99';

  React.useEffect(() => {
    if (!mobile || !userName) navigate('/forgot-password');
  }, [mobile, userName, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || !confirmPassword) {
      setError('Both fields are required.');
      return;
    }
    if (password.trim() !== confirmPassword.trim()) {
      setError('Password & Confirm Password do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('api/TOKEN/reset-password-client', { mobile, password });
      navigate('/login', {
        state: { message: 'Password reset successfully! Please login with your new password.' },
      });
    } catch (error) {
      const errorMsg = handleCatch(error);
      setError(errorMsg || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const passwordsMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: `linear-gradient(160deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
      }}
    >
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, pt: 2, pb: 1 }}>
        <IconButton
          onClick={() => navigate('/verify-otp', { state: { mobile } })}
          sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', width: 40, height: 40 }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* Title section */}
      <Box sx={{ px: 3, pb: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 26, fontWeight: 'bold', color: '#fff', mb: 0.5 }}>
          Reset Password
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.85)' }}>
          Create a new secure password
        </Typography>
      </Box>

      {/* White card */}
      <Box
        sx={{
          flex: 1,
          bgcolor: '#fff',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          px: 3,
          pt: 3,
          pb: 4,
          overflowY: 'auto',
        }}
      >
        {/* Lock icon */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: `${primaryColor}18`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon sx={{ fontSize: 32, color: primaryColor }} />
          </Box>
        </Box>

        {/* User info */}
        {userName && (
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: 13, color: '#555' }}>
              Username: <strong>{userName}</strong>
            </Typography>
            {mobile && (
              <Typography sx={{ fontSize: 13, color: '#555' }}>
                Mobile: <strong>+91 {mobile}</strong>
              </Typography>
            )}
          </Box>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>
            {error}
          </Alert>
        ) : null}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#555', mb: 0.5 }}>
            New Password
          </Typography>
          <TextField
            fullWidth
            autoFocus
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(''); }}
            size="small"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ fontSize: 18, color: primaryColor }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                    {showPassword ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography sx={{ fontSize: 12, fontWeight: '600', color: '#555', mb: 0.5 }}>
            Confirm Password
          </Typography>
          <TextField
            fullWidth
            type={showConfirm ? 'text' : 'password'}
            placeholder="Re-enter new password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
            size="small"
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ fontSize: 18, color: primaryColor }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm((v) => !v)} edge="end" size="small">
                    {showConfirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Passwords match indicator */}
          {passwordsMatch && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.5, color: '#4CAF50' }}>
              <CheckCircleIcon sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: 12 }}>Passwords match</Typography>
            </Box>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !password || !confirmPassword}
            sx={{
              height: 50,
              borderRadius: 25,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: 15,
              bgcolor: primaryColor,
              mt: 1,
              mb: 2,
              '&:hover': { bgcolor: secondaryColor },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Button
              onClick={() => navigate('/login')}
              sx={{ color: primaryColor, textTransform: 'none', fontWeight: 600, fontSize: 13 }}
            >
              Back to Sign In
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResetPasswordScreen;
